import React, {useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import CreditCard from '../../../../../CreditCard';
import {StyledCardDetails, StyledCardDetailsActions} from './StyledCardDetails';
import CircleIconButton from '../../../../../CircleIconButton';
import AuthenticationWindow from '../../../../CardsPage/AuthenticationWindow';
import SpinSmall from '../../../../../SpinSmall';
import DeleteCardModal from '../../../../CardPage/DeleteCardModal';
import CardDetailsModal from '../CardDetailsModal';
import {cardStatusesConstants, cardTypesConstants, SCAActionsConstants} from '../../../../../../constants';
import {bankingActions} from '../../../../../../state/actions/banking.actions';
import {cardActions} from '../../../../../../state/actions/card.actions';
import {
  cardsHelpers,
  objectHelpers,
  scaHelpers,
  systemHelpers
} from '../../../../../../utils/helpers';
import {firebaseEvents} from '../../../../../../snippets/firebase';
import {EyeIcon, SnowflakeIcon, TrashIcon} from '../../../../../../icons';

const {ACTIVE, FROZEN, PENDING, TERMINATED} = cardStatusesConstants;

const defaultPaymentDetailsData = {
  image: null,
  open: false,
  startTimer: false,
}

const defaultLimits = {
  limit: 0,
  used: 0
}

const {
  CARD_DELETE_ACTION,
  CARD_PAUSE_ACTION,
  DISPLAY_CARD_DETAILS_ACTION
} = SCAActionsConstants;

const {getAuthHeaders} = scaHelpers;
const {logEvent} = systemHelpers;

const CardDetails = ({
  cardDetails,
  cardId,
  creditCardProps = { isOnHold: false, error: false },
  onDeleteSuccess,
  onPauseSuccess,

  dispatch,
  deleteCard,
  getUserCardImages,
  pauseCard,
  employeeId,
  isAdmin,
  isSupport,
  ...rest
}) => {
  const [cardDetailsModalProps, setCardDetailsModalProps] = useState(defaultPaymentDetailsData);
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [activeAuthAction, setActiveAuthAction] = useState(null);
  const [deleteModalProps, setDeleteModalProps] = useState({loading: false, open: false});
  const [loading, setLoading] = useState(false);
  const [t] = useTranslation('main');

  const hasNoId = useMemo(() => cardDetails ? !objectHelpers.isValidValue(cardDetails?.bank_id) : false, [cardDetails]);

  const limits = useMemo(() => {
    if (!cardDetails) return defaultLimits;
    return {
      limit: cardsHelpers.getCardLimit(cardDetails)?.value || 0,
      used: cardsHelpers.getCardTotalPaymentValue(cardDetails),
    };
  }, [cardDetails]);

  const cardData = useMemo(() => ({
    number: cardDetails?.masked_pan ?? '',
    embossedName: cardDetails?.embossed_name ?? '',
  }), [cardDetails]);

  const cardNumber = useMemo(() => cardsHelpers.hideCardNumber(cardDetails?.masked_pan, 6), [cardDetails]);

  const cardStatus = useMemo(() => {
    if (!cardDetails) {
      return {
        isFrozen: false,
        isTerminated: false,
      };
    }

    const status = cardsHelpers.getStatus(cardDetails);

    return {
      isFrozen: status === FROZEN,
      isTerminated: status === TERMINATED,
    };
  }, [cardDetails]);

  const { disabledShowDetails, disabledFreezeButton } = useMemo(() => {
    if (!cardDetails || cardDetails.is_live === false || loading) {
      return {
        disabledShowDetails: true,
        disabledFreezeButton: true,
      };
    }

    const status = cardsHelpers.getStatus(cardDetails);
    const isCardOwner = employeeId === cardDetails.user_id;
    const isActive = status === ACTIVE;
    const isPendingOrTerminated = status === PENDING || status === TERMINATED;

    // Freeze is disabled if support user
    const canFreeze = !isSupport && !isPendingOrTerminated && (isAdmin || isCardOwner);

    return {
      disabledShowDetails: !(isCardOwner && isActive),
      disabledFreezeButton: !canFreeze,
    };
  }, [cardDetails, employeeId, isSupport, isAdmin, loading]);

  const disabledDeleteButton = useMemo(() => !cardId || isSupport || loading, [cardId, isSupport, loading]);

  const SCAResponseCallback = ({response, successCallback, errorCallback}) => {
    scaHelpers.SCAResponseCallback({
      response,
      scaCallback: (scaProps) => {
        setAuthWindowProps({...authWindowProps, ...scaProps});
        successCallback && successCallback();
      },
      errorCallback
    });
  }

  const finishLoading = () => setLoading(false);

  const loadCardDetails = () => {
    const {id: cardId, user_id: userId} = cardDetails;

    getUserCardImages({
      headers: getAuthHeaders(DISPLAY_CARD_DETAILS_ACTION),
      userId,
      cardId,
      successCallback: (images) => {
        const image = images?.[0]?.file
          ? `data:image/png;base64,${images[0].file}`
          : null;

        setCardDetailsModalProps({
          ...cardDetailsModalProps,
          image,
          loading: false,
          open: true,
          startTimer: true
        });
        handleCloseAuthModal();

        logEvent(firebaseEvents.VIEW_CARD_DETAILS);
      },
      errorCallback: (response) => SCAResponseCallback({response}),
    });
  }

  const handleDeleteCard = () => {
    setDeleteModalProps({...deleteModalProps, loading: true});
    deleteCard({
      id: cardId,
      headers: getAuthHeaders(CARD_DELETE_ACTION),
      successCallback: () => {
        handleCloseDeleteModal();
        logEvent(firebaseEvents.CARDS_DELETE_CARD);
        onDeleteSuccess?.();
      },
      errorCallback: (response) => {
        SCAResponseCallback({
          response,
          errorCallback: () => setDeleteModalProps({...deleteModalProps, open: true, loading: false})
        });
      }
    })
  }

  const handlePauseCard = () => {
    setLoading(true);
    pauseCard({
      id: cardId,
      headers: getAuthHeaders(CARD_PAUSE_ACTION),
      successCallback: (data) => {
        const status = data?.lock_status;
        const pauseLogEvents = {
          [ACTIVE]: firebaseEvents.CARDS_UNFREEZE_CARD,
          [FROZEN]: firebaseEvents.CARDS_FREEZE_CARD,
        };
        const pauseLogEvent = pauseLogEvents[status];

        pauseLogEvent && logEvent(pauseLogEvent);
        onPauseSuccess?.(data);
        finishLoading();
      },
      errorCallback: (response) => SCAResponseCallback({response, errorCallback: finishLoading}),
    })
  }

  const handleDisplayCardDetails = () => {
    setActiveAuthAction(DISPLAY_CARD_DETAILS_ACTION);
    setCardDetailsModalProps({...cardDetailsModalProps, loading: true, open: true, startTimer: false});
    loadCardDetails();
  }


  const handleCancelAuth = () => {
    const operationActions = {
      [CARD_DELETE_ACTION]: handleCloseDeleteModal,
      [DISPLAY_CARD_DETAILS_ACTION]: handleCloseCardDetailsModal,
      [CARD_PAUSE_ACTION]: finishLoading,
    }
    handleCloseAuthModal();
    operationActions[activeAuthAction]?.();
  }

  const handleSuccessAuth = () => {
    const operationActions = {
      [CARD_DELETE_ACTION]: handleDeleteCard,
      [CARD_PAUSE_ACTION]: handlePauseCard,
      [DISPLAY_CARD_DETAILS_ACTION]: handleDisplayCardDetails,
    }
    handleCloseAuthModal();
    operationActions[activeAuthAction]?.();
  }

  const handleCloseAuthModal = () => setAuthWindowProps({...authWindowProps, open: false});

  const handleCloseCardDetailsModal = () => {
    setCardDetailsModalProps({...cardDetailsModalProps, image: null, open: false});
  }

  const handleShowDetails = () => {
    if (disabledShowDetails) return;

    setActiveAuthAction(DISPLAY_CARD_DETAILS_ACTION);
    setCardDetailsModalProps({...cardDetailsModalProps, open: true});
    loadCardDetails();
  }

  const handleOkDeleteModal = () => {
    setActiveAuthAction(CARD_DELETE_ACTION);
    handleDeleteCard();
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalProps({...deleteModalProps, loading: false, open: false});
  }

  const handleOpenDeleteModal = () => {
    if (disabledDeleteButton) return;
    setDeleteModalProps({...deleteModalProps, loading: false, open: true});
  }

  const handleFreezeCard = () => {
    if (disabledFreezeButton) return;

    setActiveAuthAction(CARD_PAUSE_ACTION);
    handlePauseCard();
  }

  return (
    <>
      <StyledCardDetails
        direction='vertical'
        size='small'
        {...rest}
      >
        <SpinSmall spinning={loading}>
          <CreditCard
            cardData={cardData}
            isFrozen={cardStatus.isFrozen}
            isTerminated={cardStatus.isTerminated}
            hasNoId={hasNoId}
            limits={limits}
            {...creditCardProps}
          />
        </SpinSmall>

        <StyledCardDetailsActions>
          <CircleIconButton
            disabled={disabledShowDetails}
            icon={<EyeIcon />}
            onClick={handleShowDetails}
          >
            {t('showDetail')}
          </CircleIconButton>
          <CircleIconButton
            className={cardStatus.isFrozen ? 'unfreeze-card' : ''}
            disabled={disabledFreezeButton}
            icon={<SnowflakeIcon />}
            onClick={handleFreezeCard}
          >
            {t(cardStatus.isFrozen ? 'unfreeze' : 'freeze')}
          </CircleIconButton>
          <CircleIconButton
            className='delete-card'
            disabled={disabledDeleteButton}
            onClick={handleOpenDeleteModal}
            icon={<TrashIcon />}
          >
            {t('deleteCard')}
          </CircleIconButton>
        </StyledCardDetailsActions>

      </StyledCardDetails>

      <AuthenticationWindow
        {...authWindowProps}
        handleCancel={handleCancelAuth}
        onSuccess={handleSuccessAuth}
        operationName={activeAuthAction}
      />

      <DeleteCardModal
        {...deleteModalProps}
        cardNumber={cardNumber}
        onCancel={handleCloseDeleteModal}
        onOk={handleOkDeleteModal}
      />

      <CardDetailsModal
        {...cardDetailsModalProps}
        handleCancel={handleCloseCardDetailsModal}
      />
    </>
  );
}

CardDetails.propTypes = {
  cardDetails: PropTypes.shape({
    bank_id: PropTypes.string,
    expiry_date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    is_live: PropTypes.bool,
    limit_payment_month: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    masked_pan: PropTypes.string,
    status_code: PropTypes.number,
    total_payment_month: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    user_id: PropTypes.string,
  }),
  cardId: PropTypes.string,
  creditCardProps: PropTypes.shape({
    actions: PropTypes.any,
    error: PropTypes.bool,
    errorMessage: PropTypes.any,
    isOnHold: PropTypes.bool,
    variant: PropTypes.oneOf(Object.values(cardTypesConstants))
  }),
  onDeleteSuccess: PropTypes.func,
  onPauseSuccess: PropTypes.func,
}

const mapStateToProps = state => {
  const {isAdmin, isSupport} = state.user;
  const {id: employeeId} = state.user.employee;
  return {
    employeeId,
    isAdmin,
    isSupport,
  }
}

const mapDispatchToProps = {
  deleteCard: cardActions.deleteCard,
  getUserCardImages: bankingActions.getUserCardImages,
  pauseCard: cardActions.pauseCard,
}

export default connect(mapStateToProps, mapDispatchToProps)(CardDetails);


